"use client";

import { useEffect, useState } from "react";
import { api, type RelatoreWithCountDto } from "@/lib/api";

/**
 * Versione compatta del widget "Abbiamo Incontrato".
 *
 * Rispetto a <AbbiamoIncontrato>:
 * - altezza massima limitata (default 18rem) con scroll interno
 * - auto-scroll verticale lento e pausa al passaggio del mouse
 * - al click/hover l'utente può scorrere manualmente
 * - layout tag cloud identico al widget originale (font proporzionale al numero eventi)
 *
 * Pensato per stare sotto la sidebar dei media nella pagina dettaglio evento.
 */
export function AbbiamoIncontratoCompact({
  maxHeight = "18rem",
  autoScrollMs,
}: {
  /** Altezza massima del contenitore, default 18rem */
  maxHeight?: string;
  /** Durata completa dell'animazione di scroll (ms).
   *  Se non specificato, calcolato in base al numero di relatori
   *  (~ 1.5s per relatore, min 120s = 2 minuti). */
  autoScrollMs?: number;
}) {
  const [relatori, setRelatori] = useState<RelatoreWithCountDto[] | null>(null);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.relatori.withEventCount()
      .then((r) => { if (!cancelled) setRelatori(r); })
      .catch(() => { if (!cancelled) setRelatori([]); });
    return () => { cancelled = true; };
  }, []);

  if (relatori == null) {
    return <div className="text-xs text-neutral-500 italic">Caricamento…</div>;
  }
  if (relatori.length === 0) return null;

  // Stesso ordinamento e colori del widget originale
  const sorted = [...relatori].sort((a, b) => {
    const aLast = a.nome.trim().split(/\s+/).pop() || "";
    const bLast = b.nome.trim().split(/\s+/).pop() || "";
    return aLast.localeCompare(bLast, "it");
  });

  function hashColor(slug: string): string {
    let hash = 0;
    for (let i = 0; i < slug.length; i++) {
      hash = slug.charCodeAt(i) + ((hash << 5) - hash);
    }
    const r = Math.abs(hash >> 16) % 0x89;
    const g = Math.abs(hash >> 8) % 0x89;
    const b = Math.abs(hash) % 0x89;
    return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
  }

  // Duplica la lista così lo scroll può fare loop continuo senza "salto"
  // Velocità proporzionale al numero di relatori (min 120s).
  const effectiveMs = autoScrollMs ?? Math.max(120000, sorted.length * 1500);
  return (
    <div
      className="relative overflow-hidden rounded-lg"
      style={{ maxHeight }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div
        style={{
          lineHeight: 2.2,
          animation: `hero-vscroll ${effectiveMs}ms linear infinite`,
          animationPlayState: paused ? "paused" : "running",
        }}
      >
        {[...sorted, ...sorted].map((r, i) => (
          <a
            key={`${r.id}-${i}`}
            href={`/eventi/relatore/${r.slug}`}
            style={{
              fontSize: `${Math.min(10 + r.eventCount * 1.5, 22)}px`,
              color: hashColor(r.slug),
              display: "inline-block",
              margin: "0 4px 6px 4px",
              textDecoration: "none",
            }}
          >
            {r.nome}{" "}
            <span style={{ fontSize: "0.8em", opacity: 0.6 }}>({r.eventCount})</span>
          </a>
        ))}
      </div>
      {/* Fade top/bottom per suggerire lo scroll */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-6 bg-gradient-to-b from-white to-transparent" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-6 bg-gradient-to-t from-white to-transparent" />
      <style jsx>{`
        @keyframes hero-vscroll {
          0%   { transform: translateY(0); }
          100% { transform: translateY(-50%); }
        }
      `}</style>
    </div>
  );
}

